import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PersonIcon from "@mui/icons-material/PersonTwoTone";
import SegmentIcon from "@mui/icons-material/Segment";
import {
  ExpandDrawer,
  IconButton,
  Box,
  styled,
  Stack,
  Tooltip,
  Typography,
  Grid,
  Button,
  Divider,
} from "@financeable-com-au/financeable-ui";
import { SubmissionModal } from "@/components/SubmissionModal";
import { LodgeAIModal } from "@/components/LodgeAIModal";
import { SidebarNavigation } from "@/components/SidebarNavigation";
import {
  getIsAssetLoan,
  getIsCommercialOrBusinessApplication,
  getIsConsumerOrPersonalApplication,
  ERRORS_MESSAGE,
  SUCCESS_MESSAGE,
  TOOLTIP_MESSAGES,
  topbarHeight,
} from "@/constants";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import {
  applicationSelector,
  userDetailsSelector,
  getApplication,
  saveValue,
  clearState,
  createCustomer,
  clearProductsQuotes,
  updateAppStatusToWorkShop,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type {
  ApplicationFormSlice,
  ApplicationState,
  UserDetailsState,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
import type { UserSlice, UserPermissionsDocument } from "@/store/slices/types/userSlice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { sendQuoteToEmail } from "@/store/slices/quoteSlice";
// @ts-expect-error: ignoring as it is existing js file
import LoanDetails from "../LoanDetails/index";
// @ts-expect-error: ignoring as it is existing js file
import AddedEntity from "../Entity/AddedEntity";
// @ts-expect-error: ignoring as it is existing js file
import Entity from "../Entity/index";
// @ts-expect-error: ignoring as it is existing js file
import PageLoader from "../Application/PageLoader";
// @ts-expect-error: ignoring as it is existing js file
import ProductSelector from "../ProductSelector";
import AssetList from "../Asset/AssetList";
import { CustomerDetailsCondensed } from "../CustomerDetails/CustomerDetailsCondensed";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "../Utils/helperFunction";
import {
  useGetBusinessLendersQuery,
  useGetCommercialLendersQuery,
  useGetConsumerLendersQuery,
  useGetPersonalLendersQuery,
  // @ts-expect-error: ignoring as it is existing js file
} from "../../store/slices/apiSlice";
import { Loader } from "../Loader";

const floatingIconButtonWidth = "44px";

/**
 * Styles.
 */
// Lender Drawer.
const FloatingDrawer = styled(Box)`
  position: fixed;
  right: 0;
  top: ${topbarHeight};
  height: calc(100vh - ${topbarHeight});
  overflow: visible;
  z-index: 99;
`;

const OverflowExpandDrawer = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3)};
  height: calc(-${topbarHeight} + 100vh);
  overflow: auto;
  background-color: ${({ theme }) => theme.palette.neutral[100]};
`;

const FloatingIconButton = styled(IconButton)`
  width: ${floatingIconButtonWidth};
  position: absolute;
  left: -${floatingIconButtonWidth};
  top: 105px;
`;

const Content = styled(Stack)`
  width: calc(75vw - ${floatingIconButtonWidth});
`;

const SidebarNavigationContainer = styled(Box)`
  flex: 0 0 auto;
  position: sticky;
  top: ${topbarHeight};
  align-self: flex-start;
  height: calc(100vh - ${topbarHeight});
  z-index: 1;
`;

const MainContent = styled(Stack)`
  flex: 1 1 auto;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing(8)};
`;

interface QuoteActionResult {
  error?: unknown;
  payload?: {
    status_message?: string;
    data?: {
      status_message?: string;
      data?: unknown;
    };
  };
}

const Quote = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const {
    gettingQuote,
    open: lenderDrawerOpen,
    validationRequireFieldError,
    startApplicationBtnLoader,
  } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
  const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
  const noEditPermissions = !permissions?.application?.edit || !application?.canEditApplication;
  const isQuoteEdit = permissions?.application?.quote_edit;
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(application.applicationType);
  const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(application.applicationType);
  const isAssetLoan = getIsAssetLoan(application.applicationType);
  const customerRole = getCustomerRole(application.applicationType, application.entities, application.purpose);
  const [entityFieldErrors, setEntityFieldErrors] = useState<Record<string, string>>({});
  const [, setGuarantorFieldErrors] = useState<Record<string, string>>({
    customerTitle: "",
    firstName: "",
    lastName: "",
    IDNumber: "",
    dateOfBirth: "",
    IDCardNumber: "",
    phoneNumber: "",
    emailAddress: "",
    address: "",
    yearsAt: "",
    monthsAt: "",
    IDExpiryDate: "",
  });
  const [guarantorsError, setGuarantorsErrorState] = useState<Record<string, string>[]>([]);
  const [skipConsumer, setSkipConsumer] = useState(true);
  const [skipPersonal, setSkipPersonal] = useState(true);
  const [skipCommercial, setSkipCommercial] = useState(true);
  const [skipBusiness, setSkipBusiness] = useState(true);
  const [sendQuoteLoader, setSendQuoteLoader] = useState(false);

  const consumerResult = useGetConsumerLendersQuery({}, { skip: skipConsumer });
  const personalResult = useGetPersonalLendersQuery({}, { skip: skipPersonal });
  const commercialResult = useGetCommercialLendersQuery({}, { skip: skipCommercial });
  const businessResult = useGetBusinessLendersQuery({}, { skip: skipBusiness });

  const handleToggleLenderDrawer = () => {
    dispatch(saveValue({ open: !lenderDrawerOpen }));
  };

  const addCustomer = () => {
    dispatch(
      createCustomer({
        applicationId: application._id,
        entities: application?.entities,
      }),
    );
  };

  const setGuarantorsError = (errors: Record<string, string>[] | Record<string, string>) => {
    setGuarantorsErrorState(Array.isArray(errors) ? errors : [errors]);
  };

  const customer = application?.customers?.[0];
  const currentCustomerEmail = customer?.emails?.find((email) => email.status === "current");
  const isCustomerEmailMissing = !currentCustomerEmail?.address;
  const hasSelectedQuotes = (application?.productsQuotes?.length || 0) > 0;
  const isSendQuoteDisabled = !isQuoteEdit || isCustomerEmailMissing || !hasSelectedQuotes || sendQuoteLoader;
  const sendQuoteTooltip = (() => {
    if (isCustomerEmailMissing) return TOOLTIP_MESSAGES.SEND_QUOTE_ADD_CUSTOMER_EMAIL;
    if (!hasSelectedQuotes) return TOOLTIP_MESSAGES.SEND_QUOTES_SELECT_QUOTE_LIMIT;
    return "";
  })();

  const sendQuoteHandler = () => {
    if (!applicationId) return;

    setSendQuoteLoader(true);
    dispatch(sendQuoteToEmail({ applicationId })).then((res: QuoteActionResult) => {
      setSendQuoteLoader(false);
      if (res?.error) {
        dispatch(
          openSnackbar({
            message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
            intent: "danger",
          }),
        );
      } else {
        dispatch(clearProductsQuotes());
        dispatch(
          openSnackbar({
            message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.productQuoteSentToCustomers,
            intent: "success",
          }),
        );
      }
    });
  };

  const startApplicationHandler = () => {
    if (!applicationId) return;

    dispatch(
      updateAppStatusToWorkShop({
        applicationId,
        customerId: customer?._id,
      }),
    ).then((res: QuoteActionResult) => {
      if (res?.payload?.data?.data) {
        navigate(`/application/${applicationId}`);
      } else {
        dispatch(
          openSnackbar({
            message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
            intent: "danger",
          }),
        );
      }
    });
  };

  useEffect(() => {
    dispatch(saveValue({ gettingQuote: true, isQuoteOpen: true, open: false }));

    if (applicationId) {
      dispatch(getApplication(applicationId));
    }

    return () => {
      dispatch(clearState());
    };
  }, [applicationId, dispatch]);

  useEffect(() => {
    switch (application.applicationType) {
      case ApplicationType.Consumer:
        setSkipConsumer(false);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
      case ApplicationType.Personal:
        setSkipConsumer(true);
        setSkipPersonal(false);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
      case ApplicationType.Commercial:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(false);
        setSkipBusiness(true);
        break;
      case ApplicationType.Business:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(false);
        break;
      default:
        setSkipConsumer(true);
        setSkipPersonal(true);
        setSkipCommercial(true);
        setSkipBusiness(true);
        break;
    }
  }, [application.applicationType]);

  const lenderResultByType = {
    [ApplicationType.Consumer]: consumerResult,
    [ApplicationType.Personal]: personalResult,
    [ApplicationType.Commercial]: commercialResult,
    [ApplicationType.Business]: businessResult,
  };
  const lenderResult = lenderResultByType[application.applicationType] ?? { data: null, isFetching: false };
  const { data: lenderData, isFetching } = lenderResult;
  const quoteTitle = `${application.applicationType?.charAt(0).toUpperCase()}${application.applicationType?.slice(1)} quote - ${application.humanId}`;

  if (gettingQuote) {
    return (
      <Stack>
        <Loader text="Quote loading..." />
      </Stack>
    );
  }

  return (
    <Stack position="relative" flexDirection="row" width="100%">
      <Content flexDirection="row">
        <SidebarNavigationContainer>
          <SidebarNavigation />
        </SidebarNavigationContainer>
        <MainContent
          paddingY={4}
          paddingX={4}
          rowGap={6}
          minHeight="100vh"
          alignItems="flex-start"
          alignContent="flex-start"
        >
          <Typography level="title-lg" component="h6" textColor="neutral.800">
            {quoteTitle}
          </Typography>
          <Stack>
            {isCommercialOrBusinessApplication && (
              <>
                {application?.entities?.map((entity, index) => (
                  <Entity
                    key={entity?._id ?? index}
                    application={application}
                    entitys={entity}
                    entities={application.entities}
                    index={index}
                    fieldsErrors={entityFieldErrors}
                    setFieldsErrors={setEntityFieldErrors}
                    isDisableEdit={noEditPermissions}
                    hideAdvancedActions
                  />
                ))}

                <AddedEntity
                  fieldsErrors={entityFieldErrors}
                  setFieldsErrors={setEntityFieldErrors}
                  isDisableEdit={noEditPermissions}
                />

                {isAssetLoan && (
                  <Stack mb={10} width="100%">
                    <AssetList
                      applicationType={application.applicationType}
                      applicationId={application._id}
                      hideAdvancedActions
                    />
                  </Stack>
                )}

                <LoanDetails applicationType={application.applicationType} isDisableEdit={noEditPermissions} />
              </>
            )}

            {application?.customers?.length > 0 &&
              application?.customers?.map((customer, index) => (
                <Stack key={customer?._id ?? index} pb={8}>
                  <CustomerDetailsCondensed
                    customer={customer}
                    fieldErrors={guarantorsError}
                    setFieldErrors={setGuarantorsError}
                    applicationType={application.applicationType}
                    applicationId={application._id}
                    index={index}
                  />
                  <Divider />
                </Stack>
              ))}

            {application?.customers?.length < 1 && (
              <Grid
                container
                xs={12}
                id={`add${customerRole}`}
                style={{
                  margin: "0 0 30px",
                  padding: "0 0 20px",
                }}
              >
                <Grid xs={12} md={2}>
                  <Stack flexDirection="row">
                    <PersonIcon style={{ marginRight: "10px" }} />
                    <Typography level="body-md">Add {customerRole}</Typography>
                  </Stack>
                </Grid>
                <Grid xs={12} md={10}>
                  <Stack rowGap={4} flexDirection="column" alignItems="flex-start">
                    <Typography level="title-sm">Add {customerRole}</Typography>
                    <Button
                      variant="secondary"
                      disabled={noEditPermissions}
                      onClick={() => {
                        addCustomer();
                        const guarantorFieldErrors = {
                          yearsAt: "",
                          monthsAt: "",
                          IDExpiryDate: "",
                        };
                        setGuarantorFieldErrors({
                          customerTitle: "",
                          firstName: "",
                          lastName: "",
                          IDNumber: "",
                          dateOfBirth: "",
                          IDCardNumber: "",
                          phoneNumber: "",
                          emailAddress: "",
                          address: "",
                          ...(isCommercialOrBusinessApplication ? guarantorFieldErrors : undefined),
                        });
                      }}
                      intent={!!validationRequireFieldError?.customerErrors?.customerRequired ? "danger" : undefined}
                    >
                      Add {customerRole}
                    </Button>
                    {!!validationRequireFieldError?.customerErrors?.customerRequired && (
                      <Typography textColor="danger.200" level="body-xs">
                        {validationRequireFieldError.customerErrors.customerRequired}
                      </Typography>
                    )}
                    <Divider />
                  </Stack>
                </Grid>
              </Grid>
            )}

            {isConsumerOrPersonalApplication && (
              <>
                {isAssetLoan && (
                  <Stack style={{ marginBottom: "40px", width: "100%" }}>
                    <AssetList
                      applicationType={application.applicationType}
                      applicationId={application._id}
                      hideAdvancedActions
                    />
                  </Stack>
                )}

                <LoanDetails applicationType={application.applicationType} isDisableEdit={noEditPermissions} />
              </>
            )}

            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
              columnGap={3}
              width="100%"
              marginBottom={4}
            >
              <Tooltip title={sendQuoteTooltip} placement="top">
                <span>
                  <Button variant="secondary" onClick={sendQuoteHandler} disabled={isSendQuoteDisabled}>
                    {sendQuoteLoader ? "Sending..." : "Send Quote"}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="primary"
                onClick={startApplicationHandler}
                disabled={!(permissions?.application?.quote_convert && isQuoteEdit) || startApplicationBtnLoader}
              >
                {startApplicationBtnLoader ? "Starting..." : "Start Application"}
              </Button>
            </Stack>
          </Stack>
        </MainContent>
        {/* Submission modal for uploading quotes. */}
        <SubmissionModal />
        {/* LodgeAI modal for queues and errors. */}
        <LodgeAIModal />
      </Content>

      <FloatingDrawer>
        <Tooltip title="Expand lender list" placement="left">
          <FloatingIconButton
            aria-label="Toggle lender cards"
            variant="primary"
            intent="neutral"
            onClick={handleToggleLenderDrawer}
            size="lg"
          >
            <SegmentIcon />
          </FloatingIconButton>
        </Tooltip>
        <ExpandDrawer open={lenderDrawerOpen} collapsedWidth="calc(25vw)" expandedWidth="calc(-25px + 75vw)">
          <OverflowExpandDrawer>
            {!isFetching && lenderData ? (
              <ProductSelector lenderData={lenderData} />
            ) : (
              <PageLoader text="Lenders Loading" />
            )}
          </OverflowExpandDrawer>
        </ExpandDrawer>
      </FloatingDrawer>
    </Stack>
  );
};

export default Quote;
